import { Modal as BSModal } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { NewIconButton, Typography } from 'app/components';
import { i18n } from 'app/utils/i18n';

import './DismissModal.scss';

type DismissType = 'warning' | 'mandatory';

interface DismissModalProps {
  show: boolean;
  type: DismissType;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}

export const DismissModal = ({ show, type, onClose, onConfirm }: DismissModalProps) => {
  const dismissSchema = yup.object().shape({
    reason: yup.string().required(i18n('tag.dismiss.required')).min(10, i18n('tag.dismiss.reasonMinLength')),
  });

  type DismissForm = yup.InferType<typeof dismissSchema>;

  const dismissConfig: Record<DismissType, { title: string }> = {
    warning: {
      title: i18n('tag.dismiss.warning'),
    },
    mandatory: {
      title: i18n('tag.dismiss.mandatory'),
    },
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<DismissForm>({
    resolver: yupResolver(dismissSchema),
  });

  const { title } = dismissConfig[type];

  const onSubmit = (data: DismissForm) => {
    onConfirm(data.reason);
    reset();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <BSModal show={show} onHide={handleClose} centered contentClassName="dismiss-modal">
      <BSModal.Body className="dismiss-modal__content">
        <div className="dismiss-modal__header">
          <Typography variant="body-8" fontWeight="semibold">
            {title}
          </Typography>
          <NewIconButton icon="close" variant="transparent" onClick={handleClose} className="dismiss-modal__close-button" />
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <label htmlFor="dismiss-reason" className="mb-2">
            <Typography variant="body-7" fontWeight="semibold">
              {`${i18n('tag.dismiss.reason')}*`}
            </Typography>
          </label>
          <textarea id="dismiss-reason" {...register('reason')} className="form-text-area" rows={3} />

          {errors.reason && (
            <div className="text-danger mt-1">
              <Typography variant="body-5" fontWeight="semibold">
                {errors.reason.message}
              </Typography>
            </div>
          )}

          <div className="mt-3 d-flex justify-content-end gap-2">
            <button type="submit" className="btn btn-primary">
              {i18n('tag.dismiss.action')}
            </button>
          </div>
        </form>
      </BSModal.Body>
    </BSModal>
  );
};
